import { debounceTime, distinctUntilChanged, first, Subject, Subscription } from 'rxjs';
import { SortOrder } from '../../core/api/api';
import { BarcodeSearchResult, Product, ProductReference } from '../../core/api/api.entities';
import { BarcodesApi } from '../../core/api/barcodes/barcodes-api.service';
import { ProductsApi } from '../../core/api/products/products-api.service';
import { ProductWithBarcode } from '../../shared/models/product-with-barcode.model';
import { SelectedProductState } from '../../shared/models/selected-product-state.model';
import { convertProductReferenceToProductOption } from '../../shared/utils/product.utils';
import { BaseFormControls } from '../form-controls/base.form-controls';
import { SelectProductFormControls } from '../form-controls/select-product.form-controls';
import { SelectProductFormOptions } from '../form-options/select-product.form-options';
import { BaseForm } from './base.form';

export class SelectProductForm extends BaseForm<SelectProductFormControls> {
  formGroup = this.createFormGroup(SelectProductFormControls);
  override options = new SelectProductFormOptions();

  barcodeExists = false;
  canBeChanged = true;
  changeListener?: Subscription;
  loading = true;
  product?: ProductWithBarcode;
  productSelect = new Subject<ProductWithBarcode | undefined>();
  sort = ['productCategory.name', 'brand', 'productName'].map(column => ({ column, order: SortOrder.asc }));
  state = new SelectedProductState();

  constructor(
    private barcodesApi: BarcodesApi,
    private productsApi: ProductsApi,
    private dependingForms: BaseForm<BaseFormControls>[] = []
  ) {
    super();

    this.loadData();
  }

  get barcode(): string | null {
    return this.controls.barcode.value;
  }

  get inStockProductOptions(): ProductReference[] {
    return this.options.products.filter(product => product.inStock);
  }

  set productId(productId: number) {
    if (this.options.products.length) {
      this.productSelect.pipe(first()).subscribe(() => {
        this.resetOriginalData();
      });

      this.findProductByProductId(productId);
    } else {
      setTimeout(() => {
        this.productId = productId;
      }, 1000);
    }
  }

  override clear(): void {
    super.clear();
    this.barcodeExists = false;
  }

  clearSelectedProduct(): void {
    this.clearForms(true);
    this.product = undefined;
    this.productSelect.next(undefined);
    this.state.isLoading = false;
  }

  loadData(): void {
    this.loading = true;

    this.productsApi.findAllCompact({ sort: this.sort }).subscribe(products => {
      this.options.products = products.map(convertProductReferenceToProductOption);
      this.setBarcodeChangeListener();
      this.setProductChangeListener(products);
      this.loading = false;
    });
  }

  disable(): void {
    this.controls.barcode.disable();
    this.controls.product.disable();
  }

  enable(): void {
    if (this.canBeChanged) {
      this.controls.barcode.enable();
      this.controls.product.enable();
    }
  }

  setBarcodeChangeListener(): void {
    this.controls.barcode.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(barcode => {
      const { barcode: barcodeControl, product: productControl } = this.controls;

      if (this.state.isLoading || (barcodeControl.untouched && productControl.touched)) return;

      if (barcode) {
        this.findProductByBarcode(barcode);
      } else this.clearSelectedProduct();
    });
  }

  private setProductChangeListener(products: ProductReference[]): void {
    if (this.changeListener) {
      this.changeListener.unsubscribe();
    }

    this.changeListener = this.controls.product.valueChanges.pipe(debounceTime(300)).subscribe(name => {
      const product = this.product;
      const { barcode: barcodeControl, product: productControl } = this.controls;

      if (this.state.isLoading || (barcodeControl.value && productControl.untouched) || name === product?.detailedName)
        return;

      if (!name) {
        this.options.products = products.map(convertProductReferenceToProductOption);

        for (const form of this.dependingForms) {
          form.clear();
        }

        if (this.barcodeExists) {
          this.barcodeExists = false;
          this.controls.barcode.setValue('');
        }
      } else {
        this.options.products = products
          .filter(p => p.name.toLowerCase().includes(name.toLowerCase()))
          .map(convertProductReferenceToProductOption);

        const exactMatches = this.options.products.filter(p => p.name.toLowerCase() === name.toLowerCase());

        if (exactMatches.length === 1) {
          const product = exactMatches[0];
          this.findProductByProductId(product.id);
        }
      }
    });
  }

  protected clearForms(clearProduct: boolean = false): void {
    if (clearProduct) {
      this.clear();
    }
  }

  findProductByBarcode(barcode: string): void {
    this.disable();
    this.state.isLoadingByBarcode = true;

    this.barcodesApi.findBarcodeProduct(barcode).subscribe(({ match }: BarcodeSearchResult) => {
      if (!match) {
        this.clearForms();
        this.product = undefined;
        this.productSelect.next(undefined);
        this.state.isSubmitting = false;

        setTimeout(() => {
          this.state.isLoading = false;
          this.enable();
        }, 500);

        return;
      }

      const product: ProductWithBarcode = { ...match.product, barcode: match.barcode };

      this.controls.product.setValue(
        this.options.products.find(p => p.id === product.id)?.name || product.detailedName
      );

      this.options.products = [];
      this.barcodeExists = true;

      this.loadSelectedProduct(product);
    });
  }

  findProductByProductId(productId: number): void {
    if (this.barcodeExists) {
      this.controls.barcode.setValue(null);
    }

    this.disable();
    this.state.isLoadingByProductId = true;

    this.productsApi.findOne(productId).subscribe((product: Product) => {
      this.loadSelectedProduct(product);
    });
  }

  loadSelectedProduct(product: ProductWithBarcode): void {
    this.product = product;

    if (!this.controls.product.value) {
      this.controls.product.setValue(
        this.options.products.find(p => p.id === product.id)?.name || product.detailedName
      );
    }

    if (this.state.isSubmitting) {
      this.state.isSubmitting = false;
    } else {
      this.productSelect.next(product);
    }

    setTimeout(() => {
      this.state.isLoading = false;
      this.enable();
    }, 500);
  }
}
